import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  pfi: "PFI User",
  accounts: "Accounts",
  production: "Production",
  dispatch: "Dispatch",
  management: "Management",
  operations: "Operations",
  sales: "Sales Rep",
};

export default function Profile() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [name, setName] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [regionName, setRegionName] = useState("");

  useEffect(() => {
    api.get("/me").then((r) => { setMe(r.data); setName(r.data?.name || ""); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (me?.region_id) {
      api.get("/regions").then((r) => {
        const reg = (r.data || []).find((x) => x.id === me.region_id);
        if (reg) setRegionName(`${reg.name} (${reg.code || "—"})`);
      }).catch(() => {});
    }
  }, [me?.region_id]);

  async function saveName() {
    if (!name.trim()) return toast.error("Name cannot be empty");
    setSavingName(true);
    try { const r = await api.patch("/me", { name: name.trim() }); setMe(r.data); toast.success("Name updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingName(false); }
  }

  async function changePassword() {
    if (!currentPw || !newPw) return toast.error("Fill both password fields");
    if (newPw.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPw !== newPw2) return toast.error("Passwords do not match");
    setSavingPw(true);
    try {
      await api.post("/me/password", { current_password: currentPw, new_password: newPw });
      toast.success("Password changed");
      setCurrentPw(""); setNewPw(""); setNewPw2("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingPw(false); }
  }

  if (!me) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6 px-2 py-4" data-testid="profile-page">
      <div>
        <h1 className="heading text-2xl font-bold text-slate-900">My Profile</h1>
        <p className="text-sm text-slate-500 mt-1">Your account information and password.</p>
      </div>

      <Card className="p-5 border border-slate-200 shadow-none rounded-xl space-y-4">
        <div className="heading font-semibold text-slate-900 text-[15px]">Account</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Email</Label>
            <div className="mt-1 text-[14px] font-medium text-slate-900" data-testid="profile-email">{me.email}</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Role</Label>
            <div className="mt-1 text-[14px] font-medium text-slate-900" data-testid="profile-role">{ROLE_LABEL[me.role] || me.role}</div>
          </div>
          {me.role === "sales" && (
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Assigned Territory</Label>
              <div className="mt-1 text-[14px] font-medium text-slate-900" data-testid="profile-territory">{regionName || "Not assigned — contact admin"}</div>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Display Name</Label>
            <div className="flex gap-2 mt-1">
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
              <Button onClick={saveName} disabled={savingName || name === me.name} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="profile-save-name">Save</Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5 border border-slate-200 shadow-none rounded-xl space-y-4">
        <div>
          <div className="heading font-semibold text-slate-900 text-[15px]">Change Password</div>
          <div className="text-xs text-slate-500 mt-0.5">Minimum 8 characters. You'll stay signed in.</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Current password</Label>
            <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="mt-1" data-testid="profile-current-pw" />
          </div>
          <div />
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">New password</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1" data-testid="profile-new-pw" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Confirm new password</Label>
            <Input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} className="mt-1" data-testid="profile-confirm-pw" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={changePassword} disabled={savingPw} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="profile-change-pw">Change password</Button>
        </div>
      </Card>
    </div>
  );
}
